import { LitElement, html } from 'lit';
import {
  updateMicroStepEvent,
  displayProgressBarEvent,
  updateMacroStepEvent,
} from '@cof/refi-web-sdk/dist/utils/update-progress-bar';
import {
  EXTERNAL_ID_SESSION_STORAGE_KEY,
  isWebView,
  parseChannelFromExternalId,
  NewRelicCustomAttribute,
} from '@cof/refi-web-sdk/dist/utils/stratum';
import { REFI_MFE_UUID, trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { DebugService } from './shared/services/debug.service.js';
import { style } from './app.style.js';
import { ApplicationService } from './services/application.service.js';
import './components/personal.component.js';
import { ApiService } from './core/services/api/api.service.js';
import { REFI_PERSONAL_CONST } from './shared/constants/app.constants.js';
import { stratum, NEW_RELIC_PLUGIN_NAME } from '../constants/stratum.js';
import { Application } from './components/model/application.model.js';
import '@cof/refi-web-sdk/dist/components/transition-page/index.js';
import { environment } from '../environment/environment.js';
import './components/checkout/address-review/address-review.component.js';
import './components/checkout/income-review/income-review.component.js';
import './components/checkout/personal-checkout.component.js';
import { FeatureToggleService } from './shared/services/feature-toggle.service.js';
import { CookieService } from './core/services/cookie/cookie.service.js';

class RefiPersonalApp extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiPersonalApp');
    this.baseHref = document.querySelector('base').getAttribute('href');
    this.application = new Application();
    this.loading = true;
    this.existingExperimentIds = [];
    this.seenAnimation = false;
    this.iconicSpacing = false;
  }

  static properties = {
    application: { type: Object, reflect: true },
    loading: { type: Boolean, reflect: true },
    customerOffer: { type: Object, attribute: 'customer-offer' },
    matchingState: { type: Object, attribute: 'matching-state' },
    clientCorrelationId: { type: String, attribute: 'client-correlation-id' },
    progressBarMacroStep: {
      type: String,
      attribute: 'progress-bar-macro-step',
      reflect: true,
    },
    upfrontOffers: { type: Boolean, attribute: 'upfront-offers' },
    experiments: { type: Object },
    checkout: { type: Boolean, attribute: 'checkout-page' },
    personalCheckout: { type: Boolean, attribute: 'personal-checkout' },
    addressCheckout: { type: Boolean, attribute: 'address-checkout' },
    incomeCheckout: { type: Boolean, attribute: 'income-checkout' },
    featureToggles: { type: Object, attribute: 'feature-toggles' },
    iconicSpacing: { type: Boolean, attribute: 'iconic-spacing' },
  };

  static styles = [style];

  connectedCallback() {
    super.connectedCallback();
    this.updateApplication(this.application);
    ApiService.headers['Client-Correlation-Id'] =
      this.clientCorrelationId || REFI_MFE_UUID.makeUUID();
    displayProgressBarEvent(true);
    if (!this.upfrontOffers) {
      updateMacroStepEvent(
        this.progressBarMacroStep ||
          REFI_PERSONAL_CONST.PROGRESS_BAR_MICRO_STEPS.macroStepName,
        REFI_PERSONAL_CONST.PROGRESS_BAR_MICRO_STEPS.microStepsList
      );
      updateMicroStepEvent('');
    } else {
      updateMacroStepEvent(
        this.progressBarMacroStep ||
          REFI_PERSONAL_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.macroStepName,
        REFI_PERSONAL_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.microStepsList
      );
      const beginMicroStepIndex =
        REFI_PERSONAL_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.microStepsList.findIndex(
          (step) => step === 'name'
        );
      updateMicroStepEvent(
        REFI_PERSONAL_CONST.UFO_PROGRESS_BAR_MICRO_STEPS.microStepsList[
          beginMicroStepIndex - 1
        ]
      );
    }

    ApplicationService.isUpfrontOffers = this.upfrontOffers;

    // Nonprod experiment override
    this.experimentParam = new URL(window.location.href).searchParams.get(
      'experiment'
    );
    if (this.experimentParam && !REFI_PERSONAL_CONST.isProdEnv()) {
      this.experiments = {
        Iconic: {
          allocation: this.experimentParam,
          isEligible: true,
        },
      };
      this.debug.log(
        'Updated experiment allocation experiment to ',
        this.experimentParam
      );
      this.requestUpdate();
    }
  }

  handleCustomerOfferChange(changes) {
    if (changes.has('customerOffer') && this.customerOffer !== null) {
      ApplicationService.CustomerOffer = this.customerOffer;
    }
  }

  handleMatchingStateChange(changes) {
    if (changes.has('matchingState') && this.matchingState !== null) {
      ApplicationService.MatchingState = this.matchingState;
    }
  }

  handleFeatureTogglesChange(changes) {
    if (
      changes.has('featureToggles') &&
      this.featureToggles !== null &&
      this.featureToggles.length !== 0
    ) {
      FeatureToggleService.setFeatureToggles(this.featureToggles);
    }
  }

  updated(changes) {
    super.updated(changes);

    this.handleCustomerOfferChange(changes);
    this.handleMatchingStateChange(changes);
    this.handleFeatureTogglesChange(changes);

    if (changes.has('experiments') && this.experiments !== null) {
      // loop through this.experiments and add the object name to experimentIds array if it's eligible
      const experimentIds = [];
      if (this.experiments[REFI_PERSONAL_CONST.NDI_EXPERIMENT]) {
        experimentIds.push(
          this.experiments[REFI_PERSONAL_CONST.NDI_EXPERIMENT].allocation
        );
      }
      let removeExperimentType = '';
      Object.keys(this.experiments).forEach((experiment) => {
        if (experiment.toLowerCase().includes('iconic')) {
          if (
            this.experiments[experiment].allocation
              .toLowerCase()
              .includes(REFI_PERSONAL_CONST.ICONIC_V2)
          ) {
            // Do not set to iconic-ineligible if iconic prequal v2
            experimentIds.push(this.experiments[experiment].allocation);
          } else if (this.experiments[experiment].isEligible) {
            // Set the experiment to iconic-ineligible if it was iconic
            experimentIds.push(this.experiments[experiment].allocation);
            this.existingExperimentIds.push(
              `${experiment.toLowerCase()}-ineligible`
            );
          } else {
            experimentIds.push(`${experiment}-ineligible`);
            removeExperimentType = experiment;
          }
        }
      });
      ApplicationService.updateExperiments(
        experimentIds,
        this.existingExperimentIds,
        removeExperimentType
      );
      this.existingExperimentIds = experimentIds;

      ApplicationService.updateNDITestToggle();
      ApplicationService.updateIconicV2();
      FeatureToggleService.updateIsIconicAndToggledOn(false).then(() => {
        this.dispatchEvent(
          new CustomEvent('host-app-event', {
            bubbles: true,
            composed: true,
            detail: {
              actionName:
                REFI_PERSONAL_CONST.hostAppEventActionName
                  .initiateAnimationCompletion,
              data: {
                showCompletionAnimation: true,
                skipCurtainCloseAnimation: false,
              },
            },
          })
        );
        this.loading = false;
        this.fireApplicationAnalytics();
        this.iconicSpacing =
          ApplicationService.isIconicAndToggledOn ||
          ApplicationService.isIconicV2;
      });
    } else if (!environment.isProdEnv) {
      this.loading = false;
    }
  }

  render() {
    this.debug.log('rendered', this.baseHref, window.location.pathname);
    if (
      (this.checkout && this.personalCheckout) ||
      (!environment.isProdEnv &&
        window.location.pathname === '/nonprod/review/personal')
    ) {
      return html`
        <div role="main">
          <refi-personal-checkout></refi-personal-checkout>
        </div>
      `;
    }
    if (
      (this.checkout && this.addressCheckout) ||
      (!environment.isProdEnv &&
        window.location.pathname === '/nonprod/review/address')
    ) {
      return html`
        <div role="main">
          <refi-address-review></refi-address-review>
        </div>
      `;
    }
    if (
      (this.checkout && this.incomeCheckout) ||
      (!environment.isProdEnv &&
        window.location.pathname === '/nonprod/review/income')
    ) {
      return html`
        <div role="main">
          <refi-income-review></refi-income-review>
        </div>
      `;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (!environment.isProdEnv) {
      if (searchParams.has('force-invisible-pq')) {
        ApplicationService.isIconicAndToggledOn = true;
      }
    }
    return html`
      <div role="main">
        ${this.loading
          ? html`<refi-loading-spinner
              message="Loading..."
            ></refi-loading-spinner>`
          : html`
              <refi-personal
                ?iconic-spacing="${this.iconicSpacing}"
              ></refi-personal>
            `}
      </div>
    `;
  }

  // done this way to allow manual update of the property. do not delete
  updateApplication(app) {
    this.application = app;
    ApplicationService.setApplication(app);
    this.requestUpdate();
  }

  fireApplicationAnalytics(appObject = this.application) {
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.PRODUCT_TYPE,
      ApplicationService.CustomerOffer?.offerTypeCode ?? 'BAU'
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.IS_WEBVIEW,
      isWebView(navigator.userAgent) // null if false
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.EXTERNAL_ID,
      sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY)
    );
    stratum.setPluginVar(
      NEW_RELIC_PLUGIN_NAME,
      NewRelicCustomAttribute.CHANNEL,
      parseChannelFromExternalId(
        sessionStorage.getItem(EXTERNAL_ID_SESSION_STORAGE_KEY)
      )
    );
    const parsedCookie = CookieService.getCookie(
      REFI_PERSONAL_CONST.MARKETING_ATTRIBUTE_COOKIE
    )?.split(/\^|\|/);
    const externalIdFromCookie = parsedCookie?.[1];
    if (externalIdFromCookie && parsedCookie?.[0] === 'external_id') {
      stratum.setExternalVar('marketingChannel', externalIdFromCookie);
    }
    // eslint-disable-next-line no-restricted-syntax
    for (const key in appObject) {
      // eslint-disable-next-line no-prototype-builtins
      if (appObject.hasOwnProperty(key)) {
        const value = appObject[key];
        if (typeof value === 'object' && value !== null) {
          // Recursively check nested objects
          this.fireApplicationAnalytics(value);
        } else if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          value !== 0
        ) {
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
            replacements: {
              value: key,
            },
          });
          trackAnalyticsEvent(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, key);
        }
      }
    }
  }
}

customElements.define('refi-personal-app', RefiPersonalApp);
